import {useTranslation} from '@/translations/i18n.tsx'
import {FC, useState} from 'react'
import {UserEducationFormValidationSchema, UserExtended} from '@/features/users/types.ts'
import dayjs, {formatDate, formatLocaleDate} from '@/dayjs.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, File02Icon} from '@/components/ui/icon/Icon'
import {UserQualificationsList} from '@/features/users/components/user-qualifications-list/UserQualificationsList'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {UserEducationForm} from '@/features/users/components/user-education-form/UserEducationForm'
import {DetailsField, DetailsFieldsGrid, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {z} from 'zod'
import {DetailsInformationObservedField} from '@/components/commons/details-information-observed-field/DetailsInformationObservedField'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {Button} from '@/components/ui/button/Button'
import {zodResolver} from '@hookform/resolvers/zod'
import {
    cogeapsCertificationToLabel,
    getUserDirtyFieldsFromFormValues,
    remapUserToDefaultValues,
    remapUserToPayload
} from '@/features/users/utils'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {Text} from '@/components/ui/text/Text'

interface UserDetailsInformationProfessionalDataProps {
    user: UserExtended
}

type UserEducationFormValidationKey = keyof z.infer<typeof UserEducationFormValidationSchema>

export const UserDetailsInformationProfessionalData: FC<UserDetailsInformationProfessionalDataProps> = ({user}) => {
    const form = useForm<UserEducationFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserEducationFormValidationSchema),
        defaultValues: UserEducationFormValidationSchema.parse(remapUserToDefaultValues(user))
    })

    return (
        <FormProvider {...form}>
            <UserDetailsInformationProfessionalDataContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationProfessionalDataContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('professional-data')
    const form = useFormContext<UserEducationFormValidationSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const [isQualificationAccordionOpen, setIsQualificationAccordionOpen] = useState(false)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserEducationFormValidationSchema
    })

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('users:professional_data')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="professional-data"
                    isConfirmDisabled={!form.formState.isDirty}
                    onCancel={form.reset}
                    onConfirm={handleSubmit}
                />
            }
        >
            {isEditModeEnabled ? (
                <UserEducationForm />
            ) : (
                <DetailsFieldsGrid>
                    <DetailsField>
                        <Text size="textSm" palette="neutral" shade="500">
                            {t('users:professional_register')}
                        </Text>
                        <DetailsInformationObservedField<UserEducationFormValidationKey>
                            name="registrationNumber"
                            hideIcons={['success']}
                            value={
                                user.registrationNumber && (
                                    <Text palette="neutral" shade="950" weight="500" size="textMd">
                                        {t('users:n_x_in_x_at_x', {
                                            number: user.registrationNumber,
                                            city: user.registrationLocation,
                                            date: user.registrationDate
                                                ? formatLocaleDate(user.registrationDate, 'DD/MM/YY')
                                                : '-'
                                        })}
                                    </Text>
                                )
                            }
                        />
                    </DetailsField>

                    <DetailsField>
                        <Flexbox direction="column" gap={1} fullWidth>
                            {!user.curriculum && (
                                <Text palette="neutral" shade="950" weight="500" size="textMd">
                                    {t('users:cv')}
                                </Text>
                            )}

                            <DetailsInformationObservedField<UserEducationFormValidationKey>
                                name="curriculumAttachment"
                                hideIcons={['success']}
                                value={
                                    user.curriculum?.url && (
                                        <ButtonLink
                                            variant="tertiary"
                                            shape="text"
                                            size="lg"
                                            disabled={!user.curriculum?.url}
                                            to={user.curriculum?.url ?? ''}
                                            target="_blank"
                                        >
                                            <File02Icon />
                                            {t('users:cv')}
                                        </ButtonLink>
                                    )
                                }
                            />

                            {user.curriculumLastUpdate && (
                                <Flexbox direction="column" gap={1}>
                                    <Text palette="neutral" shade="500" size="textSm">
                                        {t('commons:updated_at', {
                                            date: formatLocaleDate(user.curriculumLastUpdate, 'DD/MM/YY')
                                        })}
                                    </Text>
                                    {user.fieldsToUpdate?.includes('curriculumLastUpdate') && (
                                        <Text palette="warning" shade="700" weight="400" size="textSm">
                                            <AlertCircleIcon />
                                            {t('users:to_update')}
                                        </Text>
                                    )}
                                </Flexbox>
                            )}
                        </Flexbox>
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('users:professional_insurance')}
                        </Text>

                        <DetailsInformationObservedField<UserEducationFormValidationKey>
                            name="professionalInsuranceLastVerify"
                            fieldsToUpdate={user.fieldsToUpdate}
                            value={
                                user.professionalInsuranceLastVerify &&
                                t('commons:last_verify_year', {
                                    year: formatLocaleDate(user.professionalInsuranceLastVerify, 'YYYY')
                                })
                            }
                        />
                    </DetailsField>

                    <DetailsField>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('users:absence_incompatibility')}
                        </Text>
                        <DetailsInformationObservedField<UserEducationFormValidationKey>
                            name="absenceOfIncompatibilityLastVerify"
                            fieldsToUpdate={user.fieldsToUpdate}
                            value={
                                user.absenceOfIncompatibilityLastVerify &&
                                t('commons:last_verify_year', {year: user.absenceOfIncompatibilityLastVerify})
                            }
                        />
                    </DetailsField>

                    <DetailsField style={{gridColumn: 'span 2'}}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('users:ecm')}
                        </Text>
                        <DetailsInformationObservedField<UserEducationFormValidationKey>
                            name="ecmScore"
                            hideIcons={['success']}
                            fieldsToUpdate={user.fieldsToUpdate}
                            value={
                                user.ecmScore && (
                                    <Text palette="neutral" shade="950" weight="500" size="textMd">
                                        {t('users:ecm_score_details', {
                                            score: user.ecmScore.score,
                                            years: `${formatDate(user.ecmScore.startDate, 'YYYY')} - ${dayjs(user.ecmScore.endDate).subtract(1, 'year').format('YYYY')}`,
                                            cogeapsCertification: user.ecmScore.cogeapsCertification
                                                ? t(cogeapsCertificationToLabel[user.ecmScore.cogeapsCertification])
                                                : t('users:no_cogeaps_certification')
                                        })}
                                    </Text>
                                )
                            }
                        />
                    </DetailsField>

                    <DetailsField style={{gridColumn: 'span 2'}}>
                        <Text palette="neutral" shade="500" size="textSm">
                            {t('commons:education')}
                        </Text>
                        <Flexbox direction="column" gap={3} fullWidth>
                            <DetailsInformationObservedField<UserEducationFormValidationKey>
                                name="qualifications"
                                fieldsToUpdate={user.fieldsToUpdate}
                                hideIcons={['success']}
                                value={
                                    user?.qualifications &&
                                    user.qualifications.length > 0 && (
                                        <Button
                                            onClick={() => setIsQualificationAccordionOpen(prev => !prev)}
                                            variant="tertiary"
                                            shape="text"
                                            size="lg"
                                        >
                                            {t('users:n_qualification', {count: user.qualifications.length})}
                                            {isQualificationAccordionOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                        </Button>
                                    )
                                }
                            />

                            {isQualificationAccordionOpen && <UserQualificationsList />}
                        </Flexbox>
                    </DetailsField>

                    {user.workSuitableUpTo && (
                        <DetailsField>
                            <Text palette="neutral" shade="500" size="textSm">
                                {t('users:eligible_until')}
                            </Text>
                            <Text weight="500" size="textMd" style={{textTransform: 'capitalize'}}>
                                {formatLocaleDate(user.workSuitableUpTo, 'MMMM YYYY')}
                            </Text>
                        </DetailsField>
                    )}
                </DetailsFieldsGrid>
            )}
        </DetailsFieldsSection>
    )
}
